"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import { RefreshCw } from "lucide-react";

/**
 * global-error.tsx — root-level React error boundary (App Router).
 *
 * Next.js catches errors at the React level (including server action
 * UnrecognizedActionError) *before* they propagate to window.onerror,
 * so posthog-js's capture_exceptions autocapture misses them.
 *
 * This boundary receives those errors and manually ships them to PostHog
 * so they appear in PostHog's Error Tracking alongside browser-caught errors.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    posthog.captureException(error, {
      $exception_type: error.name,
      $exception_message: error.message,
      digest: error.digest,
      source: "global-error-boundary",
    });
  }, [error]);

  const isStaleAction = error.message?.includes("was not found on the server");

  return (
    <html lang="en">
      <body className="bg-zinc-950 text-white antialiased flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl border border-zinc-800 bg-zinc-900 p-8 text-center space-y-4">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
            <RefreshCw className="h-6 w-6 text-red-400" />
          </div>

          {isStaleAction ? (
            <>
              <h1 className="text-lg font-semibold">MissionCtrl was updated</h1>
              <p className="text-sm text-zinc-400">
                A new version was deployed while you had this tab open. Refresh
                to get the latest version and try again.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="w-full rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-zinc-900 hover:bg-zinc-100 transition-colors"
              >
                Refresh page
              </button>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold">Something went wrong</h1>
              <p className="text-sm text-zinc-400">
                An unexpected error occurred. Our team has been notified.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={reset}
                  className="flex-1 rounded-lg border border-zinc-700 px-4 py-2.5 text-sm font-medium hover:bg-zinc-800 transition-colors"
                >
                  Try again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="flex-1 rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-zinc-900 hover:bg-zinc-100 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </>
          )}
        </div>
      </body>
    </html>
  );
}
